import React, { useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ChartContainer } from '@/components/charts/ChartContainer';
import { Container } from '@/components/legacy-ui';
import { Spinner } from '@/components/ui/spinner';
import {
  CostWindow,
  TenantCostResponse,
  useGetAccountCost,
} from '@/hooks/useAccountCost';
import { useIntegrationDisplay } from '@/hooks/useIntegrationDisplay';
import { DateRangePicker } from '@/sections/settings/subscription/components/DateRangePicker';
import { presetRange } from '@/sections/settings/subscription/constant';
import { cn } from '@/utils/classname';
import { formatPrettyName } from '@/utils/mappings.util';

type ViewMode = 'table' | 'chart';

interface CostInsightsProps {
  email?: string;
  estimatedCost?: number;
}

export function CostInsights({ email, estimatedCost = 0 }: CostInsightsProps) {
  const [selectedDays, setSelectedDays] = useState<number | null>(28);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('table');

  const isCustomRange = customFrom !== '' && customTo !== '';
  const range = isCustomRange
    ? { from: customFrom, to: customTo }
    : selectedDays
      ? presetRange(selectedDays)
      : undefined;
  const isMultiDay = isCustomRange || (selectedDays ?? 0) > 1;

  const { data, isFetching } = useGetAccountCost({
    email,
    enabled: true,
    from: range?.from,
    to: range?.to,
  });

  const { data: seriesData } = useGetAccountCost({
    email,
    enabled: viewMode === 'chart' && isMultiDay,
    from: range?.from,
    to: range?.to,
    series: true,
  });

  if (!data || data.windows.length === 0) {
    if (isFetching) {
      return (
        <Container className="bg-chariot-background-primary flex items-center justify-center py-12">
          <Spinner size="lg" />
        </Container>
      );
    }
    return null;
  }

  function handlePreset(days: number) {
    setSelectedDays(days);
    setCustomFrom('');
    setCustomTo('');
  }

  return (
    <CostDashboard
      data={data}
      seriesWindows={seriesData?.windows}
      estimatedCost={estimatedCost}
      selectedDays={selectedDays}
      onSelectDays={handlePreset}
      customFrom={customFrom}
      customTo={customTo}
      onCustomFrom={v => {
        setCustomFrom(v);
        setSelectedDays(null);
      }}
      onCustomTo={v => {
        setCustomTo(v);
        setSelectedDays(null);
      }}
      isMultiDay={isMultiDay}
      viewMode={viewMode}
      onViewMode={setViewMode}
      isFetching={isFetching}
    />
  );
}

function CostDashboard({
  data,
  seriesWindows,
  estimatedCost,
  selectedDays,
  onSelectDays,
  customFrom,
  customTo,
  onCustomFrom,
  onCustomTo,
  isMultiDay,
  viewMode,
  onViewMode,
  isFetching,
}: {
  data: TenantCostResponse;
  seriesWindows?: CostWindow[];
  estimatedCost: number;
  selectedDays: number | null;
  onSelectDays: (days: number) => void;
  customFrom: string;
  customTo: string;
  onCustomFrom: (v: string) => void;
  onCustomTo: (v: string) => void;
  isMultiDay: boolean;
  viewMode: ViewMode;
  onViewMode: (mode: ViewMode) => void;
  isFetching: boolean;
}) {
  const w = data.windows[0];
  if (!w) return null;

  const acv = estimatedCost || data.estimated_cost || data.acv;
  const grossMargin = data.gross_margin;
  const ct = (data.customer_type ?? '').toUpperCase();
  const showMargin = ct === 'MANAGED' || ct === 'SAAS';

  return (
    <div className={cn('space-y-4', isFetching && 'opacity-70')}>
      {/* Header + controls */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-chariot-text-primary text-lg font-semibold">
          Cost &amp; Margin
        </h2>
        <div className="flex items-center gap-3">
          {data.customer_type && (
            <span className="text-chariot-text-muted bg-chariot-background-secondary rounded px-2 py-0.5 text-xs font-medium tracking-wide uppercase">
              {data.customer_type}
            </span>
          )}
          {isMultiDay && (
            <SegmentedControl
              options={[
                { label: 'Table', value: 'table' as const },
                { label: 'Chart', value: 'chart' as const },
              ]}
              value={viewMode}
              onChange={onViewMode}
            />
          )}
          <DateRangePicker
            selectedDays={selectedDays}
            customFrom={customFrom}
            customTo={customTo}
            onPreset={onSelectDays}
            onCustomFrom={onCustomFrom}
            onCustomTo={onCustomTo}
            minDate={data.subscription?.contract_start}
          />
        </div>
      </div>

      {/* Period cost */}
      <Container className="bg-chariot-background-primary">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
          <Metric
            label="Compute"
            value={`${usdPrecise(w.scan_cost)} / ${w.period_days}d`}
            sub={`${usd(w.annual_scan_cost)} implied annual`}
          />
          <Metric
            label="AI (COGS)"
            value={`${usdPrecise(w.cogs_ai_cost)} / ${w.period_days}d`}
            sub={`${usd(w.annual_cogs_ai)} implied annual`}
          />
          <Metric
            label="COGS"
            value={`${usdPrecise(w.cogs_cost)} / ${w.period_days}d`}
            sub={`${usd(w.annual_cogs)} implied annual`}
            bold
          />
          <Metric
            label="AI (Sales)"
            value={`${usdPrecise(w.ai_sales_attributed)} / ${w.period_days}d`}
            sub={`${usd(w.annual_sales_ai)} implied annual`}
          />
          <Metric
            label="$/Asset"
            value={
              data.cost_per_asset > 0
                ? `$${data.cost_per_asset.toFixed(2)}`
                : '—'
            }
            sub="compute / asset / yr"
          />
          <Metric
            label="Gross Margin"
            value={
              showMargin && grossMargin !== null
                ? `${grossMargin.toFixed(1)}%`
                : 'N/A'
            }
            sub={
              showMargin && acv > 0
                ? `ACV $${Math.round(acv).toLocaleString()}`
                : ''
            }
            valueClass={showMargin ? marginColor(grossMargin) : ''}
          />
        </div>
      </Container>

      {/* Chart view */}
      {viewMode === 'chart' && isMultiDay && seriesWindows && (
        <CostChart windows={seriesWindows} />
      )}

      {/* Table view */}
      {viewMode === 'table' && (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Left column: cost breakdowns */}
            <div className="space-y-4">
              {/* Cost by Entity Type */}
              {w.by_entity_type && Object.keys(w.by_entity_type).length > 0 && (
                <Container className="bg-chariot-background-primary">
                  <SectionLabel>Cost by Entity Type</SectionLabel>
                  <div className="divide-chariot-stroke/50 mt-2 divide-y">
                    {Object.entries(w.by_entity_type)
                      .sort(([, a], [, b]) => b.cost - a.cost)
                      .map(([type, d]) => (
                        <div
                          key={type}
                          className="flex items-center justify-between py-2"
                        >
                          <span className="text-chariot-text-primary text-sm">
                            {formatPrettyName(type)}
                          </span>
                          <div className="flex items-center gap-4">
                            {d.job_count != null && d.job_count > 0 && (
                              <span className="text-chariot-text-muted text-xs tabular-nums">
                                {d.job_count.toLocaleString()} jobs
                              </span>
                            )}
                            <span className="text-chariot-text-muted text-xs tabular-nums">
                              {fmtDuration(d.duration_seconds)}
                            </span>
                            <span className="text-chariot-text-primary text-sm font-medium tabular-nums">
                              ${d.cost.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </Container>
              )}
            </div>

            {/* Right column: subscription */}
            {data.subscription && (
              <Container className="bg-chariot-background-primary self-start">
                <SectionLabel>Subscription</SectionLabel>
                <div className="divide-chariot-stroke/50 mt-2 divide-y">
                  {data.customer_type && (
                    <Row
                      label="Customer Type"
                      value={data.customer_type}
                      capitalize
                    />
                  )}
                  {data.subscription.tier && (
                    <Row
                      label="Tier"
                      value={data.subscription.tier}
                      capitalize
                    />
                  )}
                  {data.subscription.intelligence_tier && (
                    <Row
                      label="Intelligence"
                      value={data.subscription.intelligence_tier}
                      capitalize
                    />
                  )}
                  {data.subscription.msrp != null &&
                    data.subscription.msrp > 0 && (
                      <Row
                        label="MSRP"
                        value={`$${data.subscription.msrp.toLocaleString()}`}
                      />
                    )}
                  {data.subscription.subscribed_assets != null &&
                    data.subscription.subscribed_assets > 0 && (
                      <Row
                        label="Subscribed Assets"
                        value={data.subscription.subscribed_assets.toLocaleString()}
                      />
                    )}
                  {data.subscription.contract_start && (
                    <Row
                      label="Term"
                      value={`${data.subscription.contract_start} — ${data.subscription.contract_end || 'ongoing'}`}
                    />
                  )}
                  {(w.ai_subscription_credit_monthly != null ||
                    w.ai_budget_limit_monthly != null) && (
                    <>
                      {w.ai_subscription_credit_monthly != null && (
                        <Row
                          label="AI Credit"
                          value={`$${w.ai_subscription_credit_monthly.toLocaleString()}/mo`}
                        />
                      )}
                      {w.ai_budget_limit_monthly != null && (
                        <Row
                          label="Bifrost Limit"
                          value={`$${w.ai_budget_limit_monthly.toLocaleString()}/mo`}
                        />
                      )}
                      {w.ai_budget_usage_monthly != null &&
                        w.ai_budget_usage_monthly > 0 && (
                          <Row
                            label="AI Usage"
                            value={`$${w.ai_budget_usage_monthly.toLocaleString()}/mo`}
                          />
                        )}
                    </>
                  )}
                </div>
              </Container>
            )}
          </div>

          {/* Key facts */}
          <Container className="bg-chariot-background-primary">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Metric
                label="Scan Frequency"
                value={data.scan_frequency || '—'}
                capitalize
              />
              <Metric
                label="Contracted"
                value={
                  data.contracted_assets > 0
                    ? data.contracted_assets.toLocaleString()
                    : '—'
                }
                sub="assets"
              />
              <Metric
                label="Actual"
                value={
                  data.actual_assets > 0
                    ? data.actual_assets.toLocaleString()
                    : '—'
                }
                sub="assets"
              />
              <Metric
                label="Scans"
                value={w.scan_count > 0 ? w.scan_count.toString() : '—'}
                sub={`${w.period_days}d`}
              />
            </div>
          </Container>

          {/* Top capabilities */}
          {w.by_capability && Object.keys(w.by_capability).length > 0 && (
            <CapabilityTable
              capabilities={w.by_capability}
              scanCost={w.scan_cost}
            />
          )}
        </>
      )}
    </div>
  );
}

function CostChart({ windows }: { windows: CostWindow[] }) {
  const sorted = [...windows]
    .filter(w => w.date)
    .sort((a, b) => (a.date! < b.date! ? -1 : 1));

  if (sorted.length === 0) return null;

  const labels: Record<string, string> = {
    cogsCompute: 'Compute (COGS)',
    cogsAI: 'AI (COGS)',
    salesCompute: 'Compute (Sales)',
    salesAI: 'AI (Sales)',
  };

  const chartData = sorted.map(w => ({
    date: w.date!.slice(5),
    cogsCompute: Number((w.cogs_compute_cost ?? w.scan_cost).toFixed(2)),
    cogsAI: Number((w.cogs_ai_cost ?? 0).toFixed(2)),
    salesCompute: Number((w.sales_compute ?? 0).toFixed(2)),
    salesAI: Number((w.sales_ai ?? 0).toFixed(2)),
  }));

  return (
    <Container className="bg-chariot-background-primary">
      <SectionLabel>Daily Cost Breakdown</SectionLabel>
      <div className="mt-2 h-64">
        <ChartContainer>
          <AreaChart
            data={chartData}
            margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              className="stroke-chariot-stroke"
            />
            <XAxis
              dataKey="date"
              className="[&_tspan]:fill-chariot-text-secondary text-xs"
              tickLine={false}
              axisLine={false}
              minTickGap={30}
            />
            <YAxis
              className="[&_tspan]:fill-chariot-text-secondary text-xs"
              tickLine={false}
              axisLine={false}
              tickFormatter={v => `$${v}`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-chariot-background-primary)',
                borderColor: 'var(--color-chariot-stroke)',
                borderRadius: '0.375rem',
                fontSize: '0.75rem',
              }}
              formatter={(value: number, name: string) => [
                `$${value.toFixed(2)}`,
                labels[name] ?? name,
              ]}
            />
            <Area
              type="monotone"
              dataKey="cogsCompute"
              stackId="cost"
              stroke="#3b82f6"
              fill="#3b82f680"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="cogsAI"
              stackId="cost"
              stroke="#8b5cf6"
              fill="#8b5cf680"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="salesCompute"
              stackId="cost"
              stroke="#f59e0b"
              fill="#f59e0b80"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="salesAI"
              stackId="cost"
              stroke="#ef4444"
              fill="#ef444480"
              strokeWidth={2}
            />
          </AreaChart>
        </ChartContainer>
      </div>
    </Container>
  );
}

function CapabilityTable({
  capabilities,
  scanCost,
}: {
  capabilities: Record<string, { duration_seconds: number; job_count: number }>;
  scanCost: number;
}) {
  const { getDisplayName } = useIntegrationDisplay();
  const entries = Object.entries(capabilities).sort(
    ([, a], [, b]) => b.duration_seconds - a.duration_seconds
  );
  const totalDuration = entries.reduce(
    (sum, [, d]) => sum + d.duration_seconds,
    0
  );

  return (
    <Container className="bg-chariot-background-primary">
      <SectionLabel>Top Capabilities</SectionLabel>
      <div className="divide-chariot-stroke/50 mt-2 divide-y">
        {entries.slice(0, 8).map(([cap, d]) => {
          const cost =
            totalDuration > 0
              ? scanCost * (d.duration_seconds / totalDuration)
              : 0;
          return (
            <div key={cap} className="flex items-center justify-between py-2">
              <span className="text-chariot-text-primary text-sm">
                {getDisplayName(cap)}
              </span>
              <div className="flex items-center gap-4">
                <span className="text-chariot-text-muted text-xs tabular-nums">
                  {d.job_count.toLocaleString()} jobs
                </span>
                <span className="text-chariot-text-muted text-xs tabular-nums">
                  {fmtDuration(d.duration_seconds)}
                </span>
                <span className="text-chariot-text-primary text-sm font-medium tabular-nums">
                  {usdPrecise(cost)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </Container>
  );
}

function SegmentedControl<T extends string | number>({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: T }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="border-chariot-stroke flex rounded-md border">
      {options.map((opt, i) => (
        <button
          key={i}
          onClick={() => onChange(opt.value)}
          className={cn(
            'px-2.5 py-1 text-xs font-medium transition-colors',
            'first:rounded-l-md last:rounded-r-md',
            'border-chariot-stroke [&:not(:last-child)]:border-r',
            value === opt.value
              ? 'bg-chariot-brand-primary text-primary-foreground'
              : 'text-chariot-text-secondary hover:bg-chariot-background-secondary'
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function Metric({
  label,
  value,
  sub,
  bold,
  valueClass,
  capitalize,
}: {
  label: string;
  value: string;
  sub?: string;
  bold?: boolean;
  valueClass?: string;
  capitalize?: boolean;
}) {
  return (
    <div className="text-center">
      <p className="text-chariot-text-muted text-xs font-semibold tracking-wide uppercase">
        {label}
      </p>
      <p
        className={cn(
          'text-chariot-text-primary mt-0.5 text-lg tabular-nums',
          bold ? 'font-bold' : 'font-semibold',
          valueClass,
          capitalize && 'capitalize'
        )}
      >
        {value}
      </p>
      {sub && <p className="text-chariot-text-muted text-xs">{sub}</p>}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-chariot-text-secondary text-xs font-semibold tracking-wide uppercase">
      {children}
    </p>
  );
}

function Row({
  label,
  value,
  bold,
  capitalize,
}: {
  label: string;
  value: string;
  bold?: boolean;
  capitalize?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <span
        className={cn(
          'text-chariot-text-secondary text-sm',
          capitalize && 'capitalize'
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          'text-chariot-text-primary text-sm tabular-nums',
          bold ? 'font-bold' : 'font-medium',
          capitalize && 'capitalize'
        )}
      >
        {value}
      </span>
    </div>
  );
}

function marginColor(margin: number | null): string {
  if (margin === null) return '';
  if (margin >= 80) return 'text-chariot-stroke-neutral';
  if (margin >= 50) return 'text-chariot-stroke-high';
  return 'text-chariot-stroke-critical';
}

function usd(n: number): string {
  return `$${Math.round(n).toLocaleString()}`;
}

function usdPrecise(n: number): string {
  if (n === 0) return '$0.00';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(2)}`;
}

function fmtDuration(s: number): string {
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}
